import { Box, LoaderNew, SpacerNew, TextNew } from '@jfs/ui-new'
import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import React, { useCallback, useState } from 'react'
import { Keyboard } from 'react-native'
import { AepsButton } from '../../comps/AepsButton'
import { AepsTextInput } from '../../comps/AepsTextInput'
import { Container } from '../../comps/Container'
import { OtpTextInputWithTimer } from '../../comps/OtpTextInputWithTimer'
import { RootStore, useStores } from '../../data/store'
import { BbpsStackParamList } from '../../nav/Bbps'
import { Screens } from '../../nav/Screens'

const MOBILE_REGEX = /^[6-9]\d{9}$/ // Indian mobile: starts with 6-9, followed by 9 digits
const NAME_REGEX = /^[a-zA-Z\s]*$/ // Only alphabets and spaces

const REGEX = {
  MOBILE: /^[0-9]{10}$/,
  FIRST_NAME: /^[a-zA-Z]{1,25}(\s[a-zA-Z]{1,25})*$/,
  MIDDLE_NAME: /^([a-zA-Z]{1,25}(\.?\s[a-zA-Z]{1,25})*)?$/,
  LAST_NAME: /^[a-zA-Z]{1,25}(\s[a-zA-Z]{1,25})*$/,
}

type CustomerOnboardingRouteProp = RouteProp<BbpsStackParamList, typeof Screens.ADD_CUSTOMER>
type CustomerOnboardingNavigationProp = StackNavigationProp<BbpsStackParamList, typeof Screens.ADD_CUSTOMER>

type FlowStep = 'MOBILE_INPUT' | 'LOADING' | 'OTP_INPUT' | 'REGISTRATION_FORM'

interface INavigationParams {
  mobileNumber: string
  categoryId: number | string
  categoryName: string
  displayName: string
  productList?: any
  lrnMapping?: any
}

interface IFetchUserActionResponse {
  jfsUserId?: string
  user?: {
    name: {
      firstName: string
      middleName: string | null
      lastName: string
    }
    mobile: {
      mobileNumber: string
      countryCode: string
    }
  }
  userStatus?: string
  error?: {
    code: string
    message: string
  }
}

const RegistrationFormUI: React.FC<any> = ({
  mobileNumber,
  firstName,
  setFirstName,
  middleName,
  setMiddleName,
  lastName,
  setLastName,
  handleRegisterUser,
  isRegisterButtonDisabled,
  loading,
}) => {
  const handleNameChange = (text: string, setter: (val: string) => void) => {
    if (NAME_REGEX.test(text)) {
      setter(text)
    }
  }

  const validateAndSet = (text: string, setter: (val: string) => void, regex: RegExp) => {
    // We allow empty string so user can delete, otherwise check regex
    if (text === '' || regex.test(text)) {
      setter(text)
    }
  }

  return (
    <Box justifyContent={'space-between'} height={'100%'} padding={20}>
      <Box gap={3}>
        <Box>
          <TextNew fontSize={28}>{'Register New Customer'}</TextNew>
          <SpacerNew size={15} />
          <TextNew fontSize={16} color="BRAND_COLOR_GRAY_1">
            {`Complete registration for ${mobileNumber}`}
          </TextNew>
        </Box>
        <AepsTextInput
          placeholder="First Name"
          value={firstName}
          onChangeText={(txt) => validateAndSet(txt, setFirstName, REGEX.FIRST_NAME)}
          // onChangeText={(txt) => handleNameChange(txt, setFirstName)}
          // editable={!loading}
        />
        <SpacerNew size={15} />
        <AepsTextInput
          placeholder="Middle Name (Optional)"
          value={middleName}
          onChangeText={(txt) => validateAndSet(txt, setMiddleName, REGEX.MIDDLE_NAME)}
          // onChangeText={(txt) => handleNameChange(txt, setMiddleName)}
          // editable={!loading}
        />
        <SpacerNew size={15} />
        <AepsTextInput
          placeholder="Last Name"
          value={lastName}
          onChangeText={(txt) => validateAndSet(txt, setLastName, REGEX.LAST_NAME)}
          // onChangeText={(txt) => handleNameChange(txt, setLastName)}
          // editable={!loading}
        />
        <SpacerNew size={15} />
        <AepsTextInput placeholder="Customer Mobile Number" value={mobileNumber} editable={false} />
        <SpacerNew size={15} />
      </Box>
      <Box width={'100%'}>
        <AepsButton
          onPress={handleRegisterUser}
          title={loading ? 'Registering...' : 'Register User'}
          disabled={isRegisterButtonDisabled || loading}
        />
      </Box>
    </Box>
  )
}

export const CustomerOnboardingFlow = () => {
  const navigation = useNavigation<CustomerOnboardingNavigationProp>()
  const route = useRoute<CustomerOnboardingRouteProp>()

  const {
    categoryId: routeCategoryId,
    categoryName: routeCategoryName,
    displayName: routeDisplayName,
    productList: routeProductList,
  } = route.params as {
    categoryId: number | string
    categoryName: string
    displayName: string
    productList: any
  }

  const { BillPaymentAgentOnboard, message } = useStores((store) => ({
    BillPaymentAgentOnboard: store.BillPaymentAgentOnboard,
    message: store.message,
  }))

  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState<FlowStep>('MOBILE_INPUT')
  const [mobileNumber, setMobileNumber] = useState('9888888881')
  // const [mobileNumber, setMobileNumber] = useState('9561569457')
  const [otp, setOtp] = useState('')
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [lastName, setLastName] = useState('')
  const [authCode, setAuthCode] = useState('')

  const navigateToPayment = useCallback(
    async (params: INavigationParams) => {
      const isPrepaidMobile =
        String(params.categoryId) === '100001' ||
        params.displayName === 'Prepaid Mobile Payments' ||
        params.categoryName.toLowerCase().includes('prepaid mobile')

      const navigationPayload = { ...params, productList: routeProductList }
      setLoading(true)

      if (isPrepaidMobile) {
        try {
          const lrnMappingResponse = await BillPaymentAgentOnboard.getLRNMappingActionBC({
            mobileNumber: params.mobileNumber,
            categoryId: String(params.categoryId),
          })
          navigation.navigate(Screens.MOBILE_BILL_PAYMENTS as any, {
            ...navigationPayload,
            lrnMapping: lrnMappingResponse,
          })
        } catch (lrnError) {
          console.error('LRN Mapping failed:', lrnError)
          RootStore.message.handleError(lrnError)
          // message.setMessage('Failed to fetch circle details. Please try again.', 'ERROR')
          setCurrentStep('MOBILE_INPUT')
        }
      } else {
        navigation.navigate(Screens.RECHARGE_BILL_PAYMENTS as any, navigationPayload)
      }
      setLoading(false)
    },
    [BillPaymentAgentOnboard, navigation, message, routeProductList],
  )

  useFocusEffect(
    useCallback(() => {
      setLoading(false)
      if (currentStep === 'LOADING') {
        setCurrentStep('MOBILE_INPUT')
      }
      return () => {}
    }, [currentStep]),
  )

  const handleMobileChange = (text: string) => {
    const numericValue = text.replace(/[^0-9]/g, '') // Ensure only numbers
    setMobileNumber(numericValue)
  }

  const handleProceed = useCallback(async () => {
    if (!MOBILE_REGEX.test(mobileNumber)) {
      message.setMessage('Please enter a valid 10-digit mobile number.', 'ERROR')
      return
    }
    if (mobileNumber.length !== 10) {
      // message.setMessage('Please enter a valid 10-digit mobile number.', 'ERROR')
      return
    }
    // Keyboard.dismiss()
    setLoading(true)
    setCurrentStep('LOADING')

    try {
      const response = (await BillPaymentAgentOnboard.fetchUserActionBC({ mobileNumber })) as IFetchUserActionResponse
      console.log('')
      const navigationParams: INavigationParams = {
        categoryName: routeCategoryName,
        categoryId: routeCategoryId,
        displayName: routeDisplayName,
        mobileNumber: mobileNumber,
      }

      if (response?.jfsUserId) {
        BillPaymentAgentOnboard.setCustomerData({
          jfsUserId: response.jfsUserId,
          firstName: response.user?.name?.firstName || '',
          middleName: response.user?.name?.middleName || null,
          lastName: response.user?.name?.lastName || '',
          mobileNumber: mobileNumber,
          countryCode: response.user?.mobile?.countryCode || '+91',
          userStatus: response.userStatus || 'Active',
        })
        await navigateToPayment(navigationParams)
      } else {
        await handleResendOtp(true)
        setCurrentStep('OTP_INPUT')
      }
    } catch (error: any) {
      console.log('UI Catch Block: handle proceed function, ', error)
      console.log('UI Catch Block:', error)
      RootStore.message.handleError(error)
      const displayMessage =
        error?.message || error?.data?.error?.message || 'Something went wrong. Please try again later.'

      const errorCode = String(error?.errorCode || error?.code || error?.data?.error?.code || '')
      // const errorMessage = error?.message || 'An error occurred fetching user details.'

      if (errorCode === '30013' || errorCode === '55000' || displayMessage.includes('User does not exist')) {
        // If it's a known 'not found' or a specific error where we want OTP
        await handleResendOtp(true)
        setCurrentStep('OTP_INPUT')
      } else {
        // 4. Show error and reset to input to prevent being stuck in LOADING
        message.setMessage(String(displayMessage), 'ERROR')
        setCurrentStep('MOBILE_INPUT')
      }
    } finally {
      setLoading(false)
    }
  }, [
    mobileNumber,
    routeCategoryId,
    routeCategoryName,
    routeDisplayName,
    BillPaymentAgentOnboard,
    message,
    navigateToPayment,
  ])

  const isMobileValid = MOBILE_REGEX.test(mobileNumber)

  const handleResendOtp = useCallback(
    async (isInitialCall = false) => {
      setLoading(true)
      setOtp('')
      try {
        await BillPaymentAgentOnboard.genereteOtpBCApiActionBC({ mobileNumber })

        if (!isInitialCall) {
          // message.setMessage('OTP resent successfully.', 'POSITIVE')
        }
        setCurrentStep('OTP_INPUT')
      } catch (otpError: any) {
        // message.setMessage(otpError.message || 'Failed to send OTP. Please re-enter your mobile number.', 'ERROR')
        setCurrentStep('MOBILE_INPUT')
      } finally {
        setLoading(false)
      }
    },
    [mobileNumber, BillPaymentAgentOnboard, message],
  )
  const handleOtpChange = (text: string) => {
    setOtp(text)
  }

  const handleVerifyOtp = useCallback(async () => {
    const enteredOtp = otp
    if (enteredOtp.length !== 6) {
      // message.setMessage('Please enter a valid 6-digit OTP.', 'ERROR')
      return
    }
    Keyboard.dismiss()
    setLoading(true)
    try {
      const res = await BillPaymentAgentOnboard.verifyOtpActionBC({
        mobileNumber: mobileNumber,
        otp: enteredOtp,
      })

      const { authorizationCode } = res

      if (authorizationCode) {
        setAuthCode(authorizationCode)
        setCurrentStep('REGISTRATION_FORM')
        setOtp('')
      } else {
        throw new Error('OTP verified, but authorization code missing.')
      }
    } catch (error: any) {
      // message.setMessage(error.message || 'OTP verification failed. Please try again.', 'ERROR')
      setOtp('')
    } finally {
      setLoading(false)
    }
  }, [mobileNumber, otp, BillPaymentAgentOnboard, message])

  const handleRegisterUser = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim() || !authCode) {
      // message.setMessage('First Name and Last Name are required.', 'ERROR')
      return
    }
    Keyboard.dismiss()
    setLoading(true)

    const registrationPayload = {
      mobileNumber,
      authorizationCode: authCode,
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
    }

    try {
      await BillPaymentAgentOnboard.registerUserActionBC(registrationPayload)

      BillPaymentAgentOnboard.setCustomerData({
        jfsUserId: 'NEW_USER',
        firstName: registrationPayload.firstName,
        middleName: registrationPayload.middleName,
        lastName: registrationPayload.lastName,
        mobileNumber: mobileNumber,
        countryCode: '+91',
        userStatus: 'Active',
      })

      const navigationParams: INavigationParams = {
        mobileNumber,
        categoryId: routeCategoryId,
        categoryName: routeCategoryName,
        displayName: routeDisplayName,
      }

      await navigateToPayment(navigationParams)
    } catch (error: any) {
      // message.setMessage(error.message || 'User registration failed.', 'ERROR')
    } finally {
      setLoading(false)
    }
  }, [
    firstName,
    lastName,
    middleName,
    authCode,
    mobileNumber,
    routeCategoryId,
    routeCategoryName,
    routeDisplayName,
    BillPaymentAgentOnboard,
    message,
    navigateToPayment,
  ])

  const isRegisterButtonDisabled = !firstName.trim() || !lastName.trim()

  const renderContent = () => {
    if (currentStep === 'LOADING') {
      return (
        <Box flex={1} justifyContent="center" alignItems="center">
          <LoaderNew type="large" color="BRAND_PRIMARY_ACTION" />
          <SpacerNew size={15} />
          <TextNew>We are Processing your Request, Please wait....</TextNew>
        </Box>
      )
    }

    if (currentStep === 'REGISTRATION_FORM' && mobileNumber && authCode) {
      return (
        <RegistrationFormUI
          mobileNumber={mobileNumber}
          firstName={firstName}
          setFirstName={setFirstName}
          middleName={middleName}
          setMiddleName={setMiddleName}
          lastName={lastName}
          setLastName={setLastName}
          handleRegisterUser={handleRegisterUser}
          isRegisterButtonDisabled={isRegisterButtonDisabled}
          loading={loading}
        />
      )
    } // --- Combined MOBILE_INPUT and OTP_INPUT UI ---

    if (currentStep === 'MOBILE_INPUT' || currentStep === 'OTP_INPUT') {
      const showOtpSection = currentStep === 'OTP_INPUT'
      const isMobileInputDisabled = showOtpSection || loading // Workaround for OtpTextInputWithTimer using 'allValues' instead of a dedicated prop
      const allValuesForOtpComponent = { mobileNumber: mobileNumber }
      const onResendPressVoid = () => {
        handleResendOtp(false)
      } // Type fix for onResendOtpButtonPress

      return (
        <Box justifyContent={'space-between'} height={'100%'} padding={20}>
          <Box gap={30}>
            {/* HEADER */}
            <Box>
              <TextNew fontSize={28}>{'Add Customer'}</TextNew>
              {showOtpSection && (
                <TextNew fontSize={16} color="BRAND_COLOR_GRAY_1">
                  {`Enter the 6-digit OTP sent to +91 ${mobileNumber}`}
                </TextNew>
              )}
            </Box>
            {/* MOBILE NUMBER INPUT (Always Visible, but disabled in OTP step) */}
            <Box>
              <AepsTextInput
                placeholder="Customer Mobile Number"
                value={mobileNumber}
                onChangeText={setMobileNumber}
                editable={!isMobileInputDisabled}
                tintColor={'#757575' as any}
                keyboardType="numeric"
                maxLength={10}
              />
            </Box>
            {/* OTP INPUT SECTION (Only visible when currentStep is OTP_INPUT) */}
            {showOtpSection && (
              <Box gap={20}>
                <OtpTextInputWithTimer
                  value={otp}
                  onChangeText={handleOtpChange}
                  onResendOtpButtonPress={onResendPressVoid} // Type fix for Promise<void>
                  isResendOtpLoading={loading}
                  allValues={allValuesForOtpComponent} // Mobile Number workaround
                />

                <AepsButton
                  onPress={handleVerifyOtp}
                  title={loading ? 'Verifying...' : 'Verify OTP'}
                  disabled={loading || otp.length !== 6}
                />
              </Box>
            )}
          </Box>
          {/* BOTTOM SECTION (Containing Note and Proceed Button) */}
          <Box gap={20}>
            <Box>
              <TextNew fontSize={12}>
                {
                  'Note: Paying this bill allows JioFinance app to fetch your current and future bills so that you can view and pay them.'
                }
              </TextNew>
            </Box>
            {/* PROCEED BUTTON (Only visible when currentStep is MOBILE_INPUT) */}
            {!showOtpSection && (
              <Box width={'100%'}>
                <AepsButton
                  onPress={handleProceed}
                  title={'Proceed'}
                  disabled={loading || !isMobileValid || mobileNumber.length !== 10}
                />
              </Box>
            )}
          </Box>
        </Box>
      )
    }

    return null
  }
  return (
    <Container showFooter={false} overflow="scroll">
      {renderContent()}
    </Container>
  )
}
